import React, { useEffect, useState } from "react";
import { useFormikContext } from "formik";
import { useLocation, useNavigate, useParams } from "react-router";
import _ from "lodash";
import { toast } from "react-toastify";
import { Dialog } from "@mui/material";
import { Icon } from "@iconify/react";
import {
  VStack,
  HStack,
  ButtonPrimary1,
  ButtonPrimary,
  Error,
} from "../../../components/utils";
import { Input, Label, TextArea } from "../../../components/shared/InputField";
import { Select } from "../../../components/shared/Select";
import { SwitchButton } from "../../../components/shared/SwitchButton";
import { SelectButtonGroup } from "../../../components/shared/SelectButtonGroup";
import {
  FmvPriceType,
  TransactionDetailsData,
} from "../../../types/Transaction";
import {
  MonetaryEventState,
  RecurringIntervalUnit,
} from "../../../types/OptionBuyback";
import { useValuationDetails } from "../../../queries/Valuation";
import { getCurrencySymbol } from "../../../utils/currencyFormatter";
import { useCompanyStore } from "../../../store";
import Tooltip from "../../../components/shared/Tooltip";
import {
  deleteExerciseTransaction,
  deleteTransactionDetails,
  saveTransactionDetails,
} from "../../../api/TransactionApisV2";
import { useDeleteTransaction } from "../../../queries/transactionV2";
import AlertDialog from "../../../components/shared/AlertDialog";

interface BasicExerciseDetailsProps {
  onStepChange: (e?: number) => void;
  onBackClick: () => void;
}

const BasicExerciseDetails = (props: BasicExerciseDetailsProps) => {
  const {
    values,
    handleChange,
    handleBlur,
    errors,
    touched,
    setFieldValue,
    validateForm,
    setTouched,
  } = useFormikContext<TransactionDetailsData>();
  const currencySymbol = getCurrencySymbol();
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const valuations = useValuationDetails();
  const { mutate: deleteTransaction } = useDeleteTransaction();
  const { id, mode: paramsMode } = params;
  const queryParams = new URLSearchParams(location.search);
  const mode = queryParams.get("mode") || paramsMode;

  function touchAllFields(obj: any): any {
    if (Array.isArray(obj)) {
      return obj.map(touchAllFields);
    }
    if (obj !== null && typeof obj === "object") {
      return Object.keys(obj).reduce((acc, key) => {
        acc[key] = touchAllFields(obj[key]);
        return acc;
      }, {} as any);
    }
    return true;
  }

  const handleSubmit = async () => {
    const validationErrors = await validateForm();
    if (Object.keys(validationErrors).length === 0) {
      if (!id) {
        await saveTransactionDetails(values);
      }
      props.onStepChange();
    } else {
      setTouched(touchAllFields(values), true);
    }
  };

  const [inputDisabled, setInputDisabled] = useState(false);

  useEffect(() => {
    if (!values.eventState || values.eventState === MonetaryEventState.DRAFT) {
      setInputDisabled(false);
    } else {
      setInputDisabled(true);
    }
  }, [values.eventState]);

  const calculateEndDate = (e: any, type: "IntervalUnit" | "Interval") => {
    if (values.recurringSetting.isRecurring) {
      if (
        type === "Interval" &&
        (e.target.value === "" || parseInt(e.target.value, 10) <= 0)
      ) {
        return 0;
      }
      const startDate = new Date(values.startDate);
      const interval =
        type === "Interval"
          ? parseInt(e.target.value, 10)
          : values.recurringSetting.recurringInterval;
      const intervalUnit =
        type === "Interval"
          ? values.recurringSetting.recurringIntervalUnit
          : e.target.value;

      const endDate = new Date(startDate);

      switch (intervalUnit) {
        case RecurringIntervalUnit.DAYS:
          endDate.setDate(endDate.getDate() + interval);
          break;
        case RecurringIntervalUnit.MONTH:
          endDate.setMonth(endDate.getMonth() + interval);
          break;
        case RecurringIntervalUnit.QUARTER:
          endDate.setMonth(endDate.getMonth() + interval * 3);
          break;
        case RecurringIntervalUnit.YEAR:
          endDate.setFullYear(endDate.getFullYear() + interval);
          break;
        default:
          break;
      }
      setFieldValue("endDate", endDate.toISOString().split("T")[0]);
    }
    return "";
  };
  const { companyData } = useCompanyStore();

  const handleDiscard = async () => {
    await deleteTransactionDetails();
    navigate("/options/esopTransactions");
  };

  const [deleteDialogParams, setDeleteDialogParams] = useState<{
    open: boolean;
  }>({ open: false });

  return (
    <VStack className="w-full">
      <VStack className="justify-between gap-1 pb-4 pl-6 font-medium border-b-[0.5px] pt-7">
        <HStack className="flex justify-between font-semibold text-lg text-gray-900">
          Exercise - Basic Details
        </HStack>
        <HStack className="flex font-medium text-sm text-gray-500">
          Set up the name, exercise window, and key parameters for this exercise
          event.
        </HStack>
      </VStack>

      <VStack className="gap-2 py-4">
        {/* ── Section 1: Basic Information ─────────────────────────── */}
        <VStack className="mx-6 my-3 border rounded-lg bg-white shadow-sm overflow-visible">
          <HStack className="items-center gap-2 px-4 py-3 border-b bg-gray-50">
            <span className="text-sm font-semibold text-gray-800">
              Basic Information
            </span>
          </HStack>
          <VStack className="px-4 pb-4 gap-4 pt-2">
            <HStack className="gap-8">
              <VStack className="flex-1">
                <Label className="required text-sm font-normal text-gray-700">
                  Exercise Name
                </Label>
                <Input
                  type="text"
                  name="eventName"
                  className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.eventName}
                  disabled={inputDisabled}
                  placeholder="Enter Exercise Name"
                />
                {touched.eventName && errors.eventName && (
                  <Error text={errors.eventName}></Error>
                )}
              </VStack>
              <VStack className="flex-1">
                <Label className="required text-sm font-normal text-gray-700">
                  Participant List
                </Label>
                <Select
                  placeholder="--Select--"
                  name="participantListMethod"
                  className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                  disabled={mode === "edit" || inputDisabled}
                  options={["Upload Excel", "Generate By Filters"]}
                  value={values.monetaryDetails.participantListMethod}
                  onChange={(e) => {
                    if (inputDisabled) return;
                    if (e.target.value === "Upload Excel") {
                      setFieldValue("monetaryDetails.isLatestFMV", false);
                      setFieldValue("recurringSetting.isRecurring", false);
                    }
                    setFieldValue(
                      "monetaryDetails.participantListMethod",
                      e.target.value
                    );
                  }}
                  onBlur={handleBlur}
                />
                {touched.monetaryDetails?.participantListMethod &&
                  errors.monetaryDetails?.participantListMethod && (
                    <Error
                      text={errors.monetaryDetails?.participantListMethod}
                    ></Error>
                  )}
                <p className="text-xs text-gray-500 mt-1">
                  &ldquo;Generate By Filters&rdquo; auto-selects eligible
                  employees; &ldquo;Upload Excel&rdquo; lets you specify
                  participants manually.
                </p>
              </VStack>
            </HStack>
            <VStack className="gap-2">
              <Label className="text-sm font-normal text-gray-700">
                Fair Market Value
              </Label>
              <SelectButtonGroup
                options={["LATEST", "CUSTOM"]}
                value={values.monetaryDetails.isLatestFMV ? "LATEST" : "CUSTOM"}
                disabled={inputDisabled}
                onChange={(option) => {
                  if (inputDisabled) return;
                  if (
                    option === "LATEST" &&
                    companyData?.companyMetaData?.typeOfTheCompany ===
                      "PRIVATE" &&
                    (!valuations.data || valuations.data?.length === 0)
                  ) {
                    toast.error(
                      "Please upload a Valuation Report to use this option"
                    );
                    return;
                  }
                  setFieldValue(
                    "monetaryDetails.isLatestFMV",
                    option === "LATEST"
                  );
                }}
                textGetter={(o) =>
                  o === "LATEST" ? "Use Latest Valuation" : "Enter Custom FMV"
                }
              />
              {values.monetaryDetails.isLatestFMV && (
                <p className="text-xs text-gray-500">
                  {companyData?.companyMetaData?.typeOfTheCompany === "PUBLIC"
                    ? "FMV is auto-populated based on the market stock price as per the selected FMV price type."
                    : "FMV is auto-populated from the latest valuation report. New offers created after any report update will reflect the updated FMV."}
                </p>
              )}
            </VStack>
            {!values.monetaryDetails.isLatestFMV && (
              <HStack className="gap-8">
                <VStack className="flex-1">
                  <Label className="text-sm font-normal text-gray-700">
                    FMV Price ({currencySymbol})
                  </Label>
                  <VStack>
                    <Input
                      type="number"
                      name="monetaryDetails.fmvPrice"
                      placeholder="Enter FMV Price"
                      disabled={inputDisabled}
                      onBlur={handleBlur}
                      onChange={(e) => {
                        if (inputDisabled) return;
                        const numericValue = e.target.value
                          ? parseFloat(e.target.value)
                          : "";
                        setFieldValue("monetaryDetails.fmvPrice", numericValue);
                      }}
                      value={
                        values.monetaryDetails.fmvPrice === 0
                          ? ""
                          : values.monetaryDetails.fmvPrice
                      }
                    />
                    {touched.monetaryDetails?.fmvPrice &&
                      errors.monetaryDetails?.fmvPrice && (
                        <Error text={errors.monetaryDetails?.fmvPrice} />
                      )}
                  </VStack>
                </VStack>
                <VStack className="flex-1" />
              </HStack>
            )}
            {values.monetaryDetails.isLatestFMV &&
              companyData?.companyMetaData?.typeOfTheCompany === "PUBLIC" && (
                <HStack className="gap-8">
                  <VStack className="flex-1">
                    <Label className="text-sm font-normal text-gray-700">
                      FMV Price Type
                    </Label>
                    <Select
                      name="monetaryDetails.fmvPriceType"
                      placeholder="--Select--"
                      className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                      onChange={handleChange}
                      options={[
                        FmvPriceType.PREVIOUS_DAY_CLOSING,
                        FmvPriceType.EXERCISE_DAY_AVERAGE,
                      ]}
                      disabled={inputDisabled}
                      textGetter={(option) => option.replaceAll("_", " ")}
                      valueGetter={(option) => option}
                      onBlur={handleBlur}
                      value={values.monetaryDetails.fmvPriceType}
                    />
                    {touched.monetaryDetails?.fmvPriceType &&
                      errors.monetaryDetails?.fmvPriceType && (
                        <Error text={errors.monetaryDetails?.fmvPriceType} />
                      )}
                  </VStack>
                  <VStack className="flex-1" />
                </HStack>
              )}
            {values.monetaryDetails.participantListMethod !==
              "Upload Excel" && (
              <HStack className="gap-8">
                <VStack className="flex-1">
                  <Label className="text-sm font-normal text-gray-700">
                    Percentage of Net Vested Options
                  </Label>
                  <Input
                    type="number"
                    className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                    placeholder="Enter Percentage"
                    disabled={inputDisabled}
                    name="monetaryDetails.percentageOfNetVestedOptions"
                    max={100}
                    min={0}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value);
                      if (!Number.isNaN(val) && val > 100) {
                        setFieldValue(
                          "monetaryDetails.percentageOfNetVestedOptions",
                          100
                        );
                      } else {
                        handleChange(e);
                      }
                    }}
                    onBlur={handleBlur}
                    value={
                      values.monetaryDetails.percentageOfNetVestedOptions === 0
                        ? ""
                        : values.monetaryDetails.percentageOfNetVestedOptions
                    }
                  />
                  {touched.monetaryDetails?.percentageOfNetVestedOptions &&
                    errors.monetaryDetails?.percentageOfNetVestedOptions && (
                      <Error
                        text={
                          errors.monetaryDetails?.percentageOfNetVestedOptions
                        }
                      />
                    )}
                  <p className="text-xs text-gray-500 mt-1">
                    Percentage of each employee&apos;s net vested options
                    eligible for this exercise.
                  </p>
                </VStack>
                <VStack className="flex-1" />
              </HStack>
            )}
          </VStack>
        </VStack>

        {/* ── Section 2: Time Period ────────────────────────────────── */}
        <VStack className="mx-6 my-3 border rounded-lg bg-white shadow-sm overflow-visible">
          <HStack className="items-center gap-2 px-4 py-3 border-b bg-gray-50">
            <span className="text-sm font-semibold text-gray-800">
              Time Period
            </span>
          </HStack>
          <VStack className="px-4 pb-4 gap-4 pt-2">
            <HStack className="gap-8">
              <div className="flex-1">
                <Label className="required text-sm font-normal text-gray-700">
                  Start Date
                </Label>
                <Input
                  type="date"
                  disabled={inputDisabled}
                  className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                  name="startDate"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={
                    values.startDate
                      ? new Date(values.startDate).toISOString().split("T")[0]
                      : ""
                  }
                />
                {touched.startDate && errors.startDate && (
                  <Error text={errors.startDate}></Error>
                )}
              </div>
              <div className="flex-1">
                <p
                  className={`text-sm font-normal inline-flex items-center gap-1 ${
                    values.eventState !== MonetaryEventState.STARTED &&
                    values.eventState !== MonetaryEventState.CLOSED &&
                    values.eventState !== MonetaryEventState.COMPLETED
                      ? "required"
                      : ""
                  }`}
                >
                  Vesting CutOff Date
                  <Tooltip
                    text={
                      "Only Options vested up to and including this date are eligible for exercise."
                    }
                    _className="w-96"
                  >
                    <Icon
                      icon="material-symbols:info-outline-rounded"
                      height={18}
                      width={18}
                      className="cursor-pointer text-slate-dark"
                    />
                  </Tooltip>
                </p>
                <Input
                  type="date"
                  disabled={inputDisabled}
                  className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                  name="vestingCutOffDate"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={
                    values.vestingCutOffDate
                      ? new Date(values.vestingCutOffDate)
                          .toISOString()
                          .split("T")[0]
                      : ""
                  }
                />
                {touched.vestingCutOffDate && errors.vestingCutOffDate && (
                  <Error text={errors.vestingCutOffDate}></Error>
                )}
              </div>
            </HStack>
            <HStack className="gap-8">
              {values.monetaryDetails.participantListMethod !==
              "Upload Excel" ? (
                <div className="flex-1 pt-4">
                  <HStack className="justify-between gap-2">
                    <Label className="text-sm font-normal text-gray-700">
                      Recurring Exercise
                    </Label>
                    <div className="justify-end">
                      <SwitchButton
                        disabled={inputDisabled}
                        className={`${
                          inputDisabled ? "cursor-not-allowed" : ""
                        }`}
                        onClick={() => {
                          if (inputDisabled) return;
                          setFieldValue(
                            "recurringSetting.isRecurring",
                            !values.recurringSetting.isRecurring
                          );
                        }}
                        onBlur={handleBlur}
                        value={values.recurringSetting.isRecurring}
                        label=""
                      />
                    </div>
                  </HStack>
                  <p className="text-xs text-gray-500 mt-1">
                    Enable to automatically repeat this exercise on a set
                    interval. It can be paused or stopped at any time.
                  </p>
                </div>
              ) : (
                <div className="flex-1 pt-4"></div>
              )}
              {values.recurringSetting.isRecurring ? (
                <VStack className="flex-1">
                  <HStack>
                    <Label className="required text-sm font-normal text-gray-700">
                      Repeat Every
                    </Label>
                  </HStack>
                  <HStack className="w-full gap-2">
                    <VStack>
                      <Input
                        type="number"
                        disabled={inputDisabled}
                        className={`${
                          inputDisabled ? "cursor-not-allowed" : ""
                        }`}
                        name="monetaryDetails.fmvPrice"
                        placeholder="Interval"
                        onBlur={handleBlur}
                        onChange={(e) => {
                          if (inputDisabled) return;
                          const numericValue = e.target.value
                            ? parseInt(e.target.value, 10)
                            : "";
                          setFieldValue(
                            "recurringSetting.recurringInterval",
                            numericValue
                          );
                          calculateEndDate(e, "Interval");
                        }}
                        value={
                          values.recurringSetting.recurringInterval === 0
                            ? ""
                            : values.recurringSetting.recurringInterval
                        }
                      />
                      {touched.recurringSetting?.recurringInterval &&
                        errors.recurringSetting?.recurringInterval && (
                          <Error
                            text={errors.recurringSetting?.recurringInterval}
                          ></Error>
                        )}
                    </VStack>
                    <VStack>
                      <Select
                        placeholder="--Select--"
                        disabled={inputDisabled}
                        className={`${
                          inputDisabled ? "cursor-not-allowed" : ""
                        }`}
                        options={[
                          RecurringIntervalUnit.DAYS,
                          RecurringIntervalUnit.MONTH,
                          RecurringIntervalUnit.QUARTER,
                          RecurringIntervalUnit.YEAR,
                        ]}
                        onBlur={handleBlur}
                        value={values.recurringSetting.recurringIntervalUnit}
                        onChange={(e) => {
                          if (inputDisabled) return;
                          setFieldValue(
                            "recurringSetting.recurringIntervalUnit",
                            e.target.value
                          );
                          calculateEndDate(e, "IntervalUnit");
                        }}
                      />
                      {touched.recurringSetting?.recurringIntervalUnit &&
                        errors.recurringSetting?.recurringIntervalUnit && (
                          <Error
                            text={
                              errors.recurringSetting?.recurringIntervalUnit
                            }
                          ></Error>
                        )}
                    </VStack>
                  </HStack>
                </VStack>
              ) : (
                <div className="flex-1">
                  <Label className="required text-sm font-normal text-gray-700">
                    End Date
                  </Label>
                  <Input
                    type="date"
                    disabled={inputDisabled}
                    className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                    name="endDate"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={
                      values.endDate
                        ? new Date(values.endDate).toISOString().split("T")[0]
                        : ""
                    }
                  />
                  {touched.endDate && errors.endDate && (
                    <Error text={errors.endDate}></Error>
                  )}
                </div>
              )}
            </HStack>
            {values.recurringSetting.isRecurring && (
              <HStack className="gap-8">
                <VStack className="flex-1">
                  <Label className="required text-sm font-normal text-gray-700">
                    Recurrence Occurrences
                  </Label>
                  <Input
                    type="number"
                    name="recurringSetting.noOfRecurrences"
                    disabled={inputDisabled}
                    className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                    onBlur={handleBlur}
                    placeholder="Number of occurrences"
                    onChange={(e) => {
                      if (inputDisabled) return;
                      const numericValue = e.target.value
                        ? parseInt(e.target.value, 10)
                        : "";
                      setFieldValue(
                        "recurringSetting.noOfRecurrences",
                        numericValue
                      );
                    }}
                    value={
                      values.recurringSetting.noOfRecurrences === 0
                        ? ""
                        : values.recurringSetting.noOfRecurrences
                    }
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Number of times this exercise repeats before closing
                    automatically. Leave blank to run indefinitely.
                  </p>
                  {touched.recurringSetting?.noOfRecurrences &&
                    errors.recurringSetting?.noOfRecurrences && (
                      <Error text={errors.recurringSetting?.noOfRecurrences} />
                    )}
                </VStack>
                <VStack className="flex-1" />
              </HStack>
            )}
          </VStack>
        </VStack>

        {/* ── Section 3: Transaction Settings ──────────────────────── */}
        <VStack className="mx-6 my-3 border rounded-lg bg-white shadow-sm overflow-visible">
          <HStack className="items-center gap-2 px-4 py-3 border-b bg-gray-50">
            <span className="text-sm font-semibold text-gray-800">
              Transaction Settings
            </span>
          </HStack>
          <VStack className="px-4 pb-4 gap-4 pt-2">
            <HStack className="gap-8">
              <div className="flex-1 pt-4">
                <HStack className="justify-between gap-2">
                  <Label className="text-sm font-normal text-gray-700">
                    Allow Employees to Declare Willingness
                  </Label>
                  <div className="justify-end">
                    <SwitchButton
                      disabled={inputDisabled}
                      className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                      onClick={() => {
                        if (inputDisabled) return;
                        setFieldValue("askWillingness", !values.askWillingness);
                      }}
                      onBlur={handleBlur}
                      value={values.askWillingness}
                      label=""
                    />
                  </div>
                </HStack>
                <p className="text-xs text-gray-500 mt-1">
                  Enabling this lets employees choose how much to exercise. If
                  disabled, they must exercise all or none.
                </p>
              </div>
              {values.askWillingness ? (
                <div className="flex-1">
                  <p className="text-sm font-normal inline-flex items-center gap-1">
                    Minimum Willingness (%)
                    <Tooltip
                      text={
                        "This sets the minimum percentage of eligible options an employee must be willing to exercise to participate."
                      }
                      _className="w-96"
                    >
                      <Icon
                        icon="material-symbols:info-outline-rounded"
                        height={18}
                        width={18}
                        className="cursor-pointer text-slate-dark"
                      />
                    </Tooltip>
                  </p>
                  <Input
                    type="number"
                    onBlur={handleBlur}
                    disabled={inputDisabled}
                    className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                    max={100}
                    name="minWillingnessPercentage"
                    placeholder="Enter Minimum Willingness"
                    onChange={(e) => {
                      setFieldValue("minWillingnessPercentage", e.target.value);
                      setFieldValue(
                        "monetaryDetails.optionWillingnessMultiplier",
                        0
                      );
                    }}
                    value={
                      values.minWillingnessPercentage === 0
                        ? ""
                        : values.minWillingnessPercentage
                    }
                  />
                  {touched.minWillingnessPercentage &&
                    errors.minWillingnessPercentage && (
                      <Error text={errors.minWillingnessPercentage}></Error>
                    )}
                </div>
              ) : (
                <div className="flex-1"></div>
              )}
            </HStack>
            {values.askWillingness && (
              <HStack className="gap-8">
                <div className="flex-1">
                  <Label className="text-sm font-normal text-gray-700">
                    Selection of Grants
                  </Label>
                  <Select
                    options={["FIFO", "Grant Price", "Custom"]}
                    disabled={inputDisabled}
                    className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                    onBlur={handleBlur}
                    value={values.monetaryDetails?.selectionOfGrants || "FIFO"}
                    onChange={(e) => {
                      if (inputDisabled) return;
                      setFieldValue(
                        "monetaryDetails.selectionOfGrants",
                        e.target.value
                      );
                    }}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {(values.monetaryDetails?.selectionOfGrants || "FIFO") ===
                    "FIFO"
                      ? "Oldest grants are exercised first."
                      : (values.monetaryDetails?.selectionOfGrants ||
                          "FIFO") === "Grant Price"
                      ? "Grants with the lowest grant price are exercised first."
                      : "Employees can choose which grants to exercise."}
                  </p>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-normal inline-flex items-center gap-1">
                    Multiples of Willingness
                    <Tooltip
                      text={
                        "Specify the allowed multiples (e.g., 10, 25) in which employees can exercise their options."
                      }
                      _className="w-96"
                    >
                      <Icon
                        icon="material-symbols:info-outline-rounded"
                        height={18}
                        width={18}
                        className="cursor-pointer text-slate-dark"
                      />
                    </Tooltip>
                  </p>
                  <Input
                    type="number"
                    disabled={inputDisabled}
                    className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                    name="optionWillingnessMultiplier"
                    onBlur={handleBlur}
                    placeholder="Multiples of"
                    onChange={(e) => {
                      if (inputDisabled) return;
                      const numericValue = e.target.value
                        ? parseFloat(e.target.value)
                        : "";
                      setFieldValue(
                        "monetaryDetails.optionWillingnessMultiplier",
                        numericValue
                      );
                      setFieldValue(
                        "monetaryDetails.minWillingnessPercentage",
                        0
                      );
                    }}
                    value={
                      values.monetaryDetails.optionWillingnessMultiplier === 0
                        ? ""
                        : values.monetaryDetails.optionWillingnessMultiplier
                    }
                  />
                </div>
              </HStack>
            )}
            <HStack className="gap-8">
              <div className="flex-1 pt-4">
                <HStack className="justify-between gap-2">
                  <Label className="text-sm font-normal text-gray-700">
                    Consider Blackout Days
                  </Label>
                  <div className="justify-end">
                    <SwitchButton
                      disabled={inputDisabled}
                      className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                      onClick={() => {
                        if (inputDisabled) return;
                        setFieldValue(
                          "monetaryDetails.considerBlackOutDays",
                          !values.monetaryDetails.considerBlackOutDays
                        );
                      }}
                      onBlur={handleBlur}
                      value={
                        values.monetaryDetails.considerBlackOutDays || false
                      }
                      label=""
                    />
                  </div>
                </HStack>
                <p className="text-xs text-gray-500 mt-1">
                  Enable this to prevent employees from exercising on blackout
                  dates as defined in the company settings.
                </p>
              </div>
              <HStack className="flex-1 py-6" />
            </HStack>
            <VStack className="flex-1">
              <Label className="text-sm font-normal text-gray-700">Notes</Label>
              <TextArea
                className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                disabled={inputDisabled}
                name="description"
                onBlur={handleBlur}
                placeholder="Add any notes or special instructions for this exercise..."
                value={values.description}
                onChange={handleChange}
              />
            </VStack>
          </VStack>
        </VStack>
      </VStack>

      <HStack className="justify-between px-6 pt-4 pb-8">
        <HStack>
          <ButtonPrimary1
            className=""
            onClick={() => {
              setDeleteDialogParams({ open: true });
            }}
          >
            Discard
          </ButtonPrimary1>
        </HStack>
        <HStack className="justify-end">
          <ButtonPrimary
            onClick={() => {
              handleSubmit();
            }}
          >
            Next
          </ButtonPrimary>
        </HStack>
      </HStack>

      <Dialog
        open={deleteDialogParams.open}
        onClose={() => setDeleteDialogParams({ open: false })}
        maxWidth="md"
      >
        {deleteDialogParams.open && (
          <AlertDialog
            message={`Are you sure you want to discard`}
            primaryActionText="Delete"
            onClose={() => setDeleteDialogParams({ open: false })}
            secondaryActionText="Cancel"
            onPrimaryAction={() => {
              handleDiscard();
              setDeleteDialogParams({ open: false });
            }}
            onSecondaryAction={() => {
              setDeleteDialogParams({ open: false });
            }}
            open={deleteDialogParams.open}
          />
        )}
      </Dialog>
    </VStack>
  );
};

export default BasicExerciseDetails;
